import type { MealSuggestion } from './meals';
import {
  MAX_RECIPE_SHOPPING_LIST_ITEMS,
  MAX_RECIPE_SHOPPING_LISTS,
  type RecipeShoppingList,
  type RecipeShoppingListHandoffReceipt,
  type RecipeShoppingListItem,
} from './model';

const SHA256_HEX = /^[a-f0-9]{64}$/;
const HANDOFF_INTENT_ID = /^hnd_[A-Za-z0-9_-]{16,124}$/u;

export const assertRecipeShoppingLists = (
  householdId: string,
  lists: RecipeShoppingList[],
): void => {
  if (lists.length > MAX_RECIPE_SHOPPING_LISTS) {
    throw new Error('RECIPE_SHOPPING_LIST_LIMIT');
  }
  const listIds = new Set<string>();
  for (const list of lists) {
    if (
      list.householdId !== householdId ||
      listIds.has(list.id) ||
      list.id.length === 0 ||
      list.id.length > 100 ||
      list.publicRecipeId.length === 0 ||
      list.publicRecipeId.length > 100 ||
      list.recipeTitle.length === 0 ||
      list.recipeTitle.length > 160 ||
      !Number.isInteger(list.portions) ||
      list.portions < 1 ||
      list.portions > 12 ||
      !SHA256_HEX.test(list.sourceReturnDigest) ||
      list.items.length > MAX_RECIPE_SHOPPING_LIST_ITEMS
    ) {
      throw new Error('RECIPE_SHOPPING_LIST_INVALID');
    }
    if (
      list.handoffReceipt != null &&
      (!['archibald', 'paniprix'].includes(list.handoffReceipt.target) ||
        list.handoffReceipt.status !== 'accepted' ||
        list.handoffReceipt.signatureVerified !== true ||
        list.handoffReceipt.sourceMayRelease !== true ||
        !HANDOFF_INTENT_ID.test(list.handoffReceipt.intentId) ||
        !Number.isFinite(Date.parse(list.handoffReceipt.verifiedAt)))
    ) {
      throw new Error('RECIPE_SHOPPING_LIST_HANDOFF_RECEIPT_INVALID');
    }
    listIds.add(list.id);
    const itemIds = new Set<string>();
    for (const item of list.items) {
      if (
        itemIds.has(item.id) ||
        item.id.length === 0 ||
        item.id.length > 100 ||
        item.ingredientId.length === 0 ||
        item.ingredientId.length > 100 ||
        item.label.length === 0 ||
        item.label.length > 160 ||
        (item.requiredQuantity != null &&
          (!Number.isFinite(item.requiredQuantity) ||
            item.requiredQuantity <= 0 ||
            item.requiredQuantity > 1_000_000))
      ) {
        throw new Error('RECIPE_SHOPPING_ITEM_INVALID');
      }
      itemIds.add(item.id);
    }
  }
};

export const recordRecipeShoppingListHandoffReceipt = (
  list: RecipeShoppingList,
  receipt: Pick<RecipeShoppingListHandoffReceipt, 'intentId' | 'verifiedAt'>,
  target: RecipeShoppingListHandoffReceipt['target'] = 'archibald',
): RecipeShoppingList => ({
  ...list,
  handoffReceipt: {
    target,
    intentId: receipt.intentId,
    status: 'accepted',
    signatureVerified: true,
    sourceMayRelease: true,
    verifiedAt: receipt.verifiedAt,
  },
  updatedAt: receipt.verifiedAt,
});

const roundQuantity = (value: number): number =>
  Math.round((value + Number.EPSILON) * 1_000) / 1_000;

const mergeItems = (items: RecipeShoppingListItem[]): RecipeShoppingListItem[] => {
  const merged = new Map<string, RecipeShoppingListItem>();
  for (const item of items) {
    const key = `${item.kind}:${item.ingredientId}:${item.unit ?? 'none'}`;
    const previous = merged.get(key);
    if (previous == null) {
      merged.set(key, item);
      continue;
    }
    merged.set(key, {
      ...previous,
      requiredQuantity:
        previous.requiredQuantity == null || item.requiredQuantity == null
          ? null
          : roundQuantity(previous.requiredQuantity + item.requiredQuantity),
    });
  }
  return [...merged.values()].slice(0, MAX_RECIPE_SHOPPING_LIST_ITEMS);
};

export const buildRecipeShoppingList = (input: {
  householdId: string;
  id: string;
  locale: 'fr-FR' | 'en';
  sourceReturnDigest: string;
  suggestion: MealSuggestion;
  now: string;
}): RecipeShoppingList => {
  const stockItems = input.suggestion.ingredientAvailability.flatMap(
    ({ ingredient, present, requiredQuantity, coveredQuantity, missingQuantity }) => {
      if (!ingredient.required) return [];
      const missing = !present
        ? requiredQuantity
        : missingQuantity != null && missingQuantity > 0
          ? missingQuantity
          : requiredQuantity != null && coveredQuantity == null
            ? null
            : 0;
      if (missing === 0) return [];
      return [{
        id: `stock-${ingredient.id}`,
        kind: 'stock' as const,
        ingredientId: ingredient.id,
        label: ingredient.label,
        requiredQuantity: missing,
        unit: ingredient.unit,
        checked: false,
      }];
    },
  );
  const pantryItems = input.suggestion.template.requiredPantryIngredients.map(
    (label, index): RecipeShoppingListItem => ({
      id: `pantry-${index}-${input.suggestion.template.id}`,
      kind: 'pantry',
      ingredientId: `pantry-${index}`,
      label,
      requiredQuantity: null,
      unit: null,
      checked: false,
    }),
  );

  return {
    id: input.id,
    householdId: input.householdId,
    publicRecipeId: input.suggestion.template.id,
    recipeTitle: input.suggestion.template.title,
    locale: input.locale,
    portions: input.suggestion.portions,
    sourceReturnDigest: input.sourceReturnDigest,
    items: mergeItems([...stockItems, ...pantryItems]),
    createdAt: input.now,
    updatedAt: input.now,
  };
};

export const setRecipeShoppingItemChecked = (
  list: RecipeShoppingList,
  itemId: string,
  checked: boolean,
  now: string,
): RecipeShoppingList => {
  if (!list.items.some((item) => item.id === itemId)) {
    throw new Error('RECIPE_SHOPPING_ITEM_NOT_FOUND');
  }
  return {
    ...list,
    items: list.items.map((item) => item.id === itemId ? { ...item, checked } : item),
    updatedAt: now,
  };
};
